import { ApplicationConfig, ErrorHandler, importProvidersFrom, provideCheckNoChangesConfig, provideZonelessChangeDetection } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_NATIVE_DATE_FORMATS } from "@angular/material/core";
import { init, browserTracingIntegration, createErrorHandler } from "@sentry/angular";

import { environment } from "src/environments/environment";
import { AuthGuard } from "src/guards/auth.guard";
import packageJSON from "../../package.json";
import { AppRoutingModule } from "./app-routing.module";
import { SharedModule } from "./shared/shared.module";
import { LivecoDateAdapter } from "./shared/utils/liveco-date-adapter";
import { provideAppTranslation } from "./translate.module";
import { KFeathers } from "./services/api/feathers.service";
import { AuthService } from "./services/auth.service";
import { LivecoAPIService } from "./services/liveco-api.service";
import { ProofreadingService } from "./services/proofreading.service";
import { ProductionTextsService } from "./services/production-texts.service";
import { DashboardHistoryService } from "./services/dashboard-history.service";
import { RequestsService } from "./services/requests.service";
import { NotificationsService } from "./services/notifications.service";
import { MembersService } from "./services/member.service";
import { FileService } from "./services/file.service";
import { StateMachineService } from "./services/machine-state.service";
import { GroupService } from "./services/groups.service";
import { ProjectService } from "./services/project.service";
import { CustomerService } from "./services/customer.service";
import { CustomerContactService } from "./services/customer-contacts.service";
import { RiverflowJobTypeService } from "./services/l2pub-job-types.service";
import { BrandCategoriesService } from "./services/brand-categories.service";

export function localeFactory(authService: AuthService): string {
    return authService.user?.lang === "en" ? "en-US" : "fr-FR";
}

init({
    // No dsn: the SDK stays disabled and sends nothing
    dsn: environment.sentry.dsn || undefined,
    environment: environment.sentry.environment,
    release: packageJSON.version,
    tracesSampleRate: 0.01,
    // No session tracking (formerly autoSessionTracking: false)
    integrations: (defaults) => [
        ...defaults.filter((integration) => integration.name !== 'BrowserSession'),
        browserTracingIntegration(),
    ],
});

/** Providers of the former AppModule, for bootstrapApplication (main.ts). */
export const appConfig: ApplicationConfig = {
    providers: [
        // No zone.js: views are checked on template events, signals, async pipes, markForCheck,
        // and after each API call or realtime event (ViewRefresher, from KFeathers)
        provideZonelessChangeDetection(),
        // Dev only: every second, report as NG0100 any binding that changed without Angular
        // being told, i.e. a spot that still needs a markForCheck, a signal or ViewRefresher
        ...(environment.production ? [] : [provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 })]),
        importProvidersFrom(AppRoutingModule, BrowserAnimationsModule, SharedModule),
        KFeathers,
        AuthGuard,
        provideAppTranslation(),
        HttpParams,
        Title,
        AuthService,
        LivecoAPIService,
        ProofreadingService,
        ProductionTextsService,
        DashboardHistoryService,
        RequestsService,
        NotificationsService,
        MembersService,
        FileService,
        StateMachineService,
        GroupService,
        ProjectService,
        CustomerService,
        CustomerContactService,
        RiverflowJobTypeService,
        BrandCategoriesService,
        { provide: MAT_DATE_LOCALE, useFactory: localeFactory, deps: [AuthService] },
        // Native date adapter that reads typed dates day first in French, for every date picker
        { provide: DateAdapter, useClass: LivecoDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
        {
            provide: ErrorHandler, useValue: createErrorHandler({
                showDialog: false,
            }),
        }
    ]
};
